import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useRecursiveTimeout from "@insite/client-framework/Common/Hooks/useRecursiveTimeout";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button, { ButtonIcon, ButtonProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useEmblaCarousel } from "embla-carousel/react";
import parse from "html-react-parser";
import React, { useCallback, useEffect, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    modalImg?: string;
    modalSummary?: any;
}

type Props = OwnProps;

export interface GAIAModalStyles {
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonProps;
    gaiaModalWrapper?: GridContainerProps;
    gaiaModalImage?: GridItemProps;
    gaiaModalSummary?: GridItemProps;
    image?: LazyImageProps;
}

export const GAIAModalStyles: GAIAModalStyles = {
    mainContainer: { gap: 0 },
    prevArrowGridItem: {
        align: "middle",
        css: css`
            justify-content: flex-start;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 2%;
                        max-width: 2%;
                    `,
                    css`
                        flex-basis: 2%;
                        max-width: 2%;
                    `,
                    css`
                        flex-basis: 4%;
                        max-width: 4%;
                    `,
                    css`
                        flex-basis: 4%;
                        max-width: 4%;
                    `,
                    css`
                        flex-basis: 2%;
                        max-width: 2%;
                    `,
                ])}
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 5px;
            background: #f8f8f8;
            z-index: 1;
            &:hover {
                background: ${getColor("primary")};
                opacity: 0.8;
                span {
                    color: #fff !important;
                }
            }
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 92%;
                        max-width: 92%;
                    `,
                    css`
                        flex-basis: 96%;
                        max-width: 96%;
                    `,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    carouselSlide: {
        css: css`
            flex: 0 0 auto;
            padding: 0;
            position: relative;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 50%;
                    `,
                    css`
                        width: 20%;
                    `,
                    css`
                        width: 20%;
                    `,
                    css`
                        width: 20%;
                    `,
                    css`
                        width: 20%;
                    `,
                ])}
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 0 20px;
            display: flex;
            flex-direction: column;
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 2%;
                        max-width: 2%;
                    `,
                    css`
                        flex-basis: 2%;
                        max-width: 2%;
                    `,
                    css`
                        flex-basis: 4%;
                        max-width: 4%;
                    `,
                    css`
                        flex-basis: 4%;
                        max-width: 4%;
                    `,
                    css`
                        flex-basis: 2%;
                        max-width: 2%;
                    `,
                ])}
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 5px;
            background: #f8f8f8;
            z-index: 1;
            &:hover {
                background: ${getColor("primary")};
                opacity: 0.8;
                span {
                    color: #fff !important;
                }
            }
        `,
    },
    gaiaModalWrapper: {
        css: css`
            position: relative;
            flex: 0 0 100%;
            overflow: hidden;
        `,
    },
    gaiaModalImage: {
        width: [12, 12, 12, 4, 4],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css``,
                    css``,
                    css`
                        display: flex;
                        justify-content: center;
                    `,
                    css``,
                    css``,
                ])}
        `,
    },
    gaiaModalSummary: {
        width: [12, 12, 12, 8, 8],
        css: css``,
    },
    image: {
        css: css`
            display: flex;
            justify-content: flex-start;
        `,
    },
};
const styles = GAIAModalStyles;
const GAIATileDetailsModal = ({ modalImg, modalSummary }: Props) => {
    return (
        <GridContainer {...styles.mainContainer}>
            {/* <GridItem {...styles.prevArrowGridItem}>
                <Button {...styles.prevArrowButton} onClick={() => embla && embla.scrollPrev()}>
                    <ButtonIcon src={ChevronLeft} />
                </Button>
            </GridItem> */}
            <GridItem {...styles.carouselGridItem}>
                <StyledWrapper {...styles.carouselWrapper}>
                    <StyledWrapper {...styles.carouselContainer}>
                        <StyledWrapper {...styles.carouselSlidesContainer}>
                            <GridContainer {...styles.gaiaModalWrapper}>
                                <GridItem {...styles.gaiaModalImage}>
                                    <LazyImage {...styles.image} src={modalImg} />
                                </GridItem>
                                <GridItem {...styles.gaiaModalSummary}>
                                    <StyledWrapper> {modalSummary}</StyledWrapper>
                                </GridItem>
                            </GridContainer>
                            <GridContainer {...styles.gaiaModalWrapper}>
                                <GridItem {...styles.gaiaModalImage}>
                                    <LazyImage {...styles.image} src={modalImg} />
                                </GridItem>
                                <GridItem {...styles.gaiaModalSummary}>
                                    <StyledWrapper> {modalSummary}</StyledWrapper>
                                </GridItem>
                            </GridContainer>
                            <GridContainer {...styles.gaiaModalWrapper}>
                                <GridItem {...styles.gaiaModalImage}>
                                    <LazyImage {...styles.image} src={modalImg} />
                                </GridItem>
                                <GridItem {...styles.gaiaModalSummary}>
                                    <StyledWrapper> {modalSummary}</StyledWrapper>
                                </GridItem>
                            </GridContainer>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </GridItem>
            {/* <GridItem {...styles.nextArrowGridItem}>
                <Button {...styles.nextArrowButton} onClick={() => embla && embla.scrollNext()}>
                    <ButtonIcon src={ChevronRight} />
                </Button>
            </GridItem> */}
        </GridContainer>
    );
};

export default GAIATileDetailsModal;
